import React, { Suspense } from "react";
import { connect } from "react-redux";
import ProgressiveImage from "react-progressive-image";
import { RdvButton } from "../../page-framework/rdv-button";
import { SVG } from "../../../component-configuration/svgs";
import { getVideoLinksFromExternalId, snowplowAnalytics } from "../../../utilities/general";
import IsVisible from "../../../utilities/is-visible-hoc";
import { AppState } from "../../../redux";
import * as Actions from "../../../redux/actions";
import Truncate from "react-truncate";
import {
  SetPlayingFullScreenPayload,
  SetPlayingFullScreenAction,
  PlayFullScreenVideoPayload,
  PlayFullScreenVideoAction,
} from "../../../redux/full-screen-video/types";
import { ProfilePictureName } from "../../page-framework/profile-picture-name";
import { ItemPreviews } from "./types";
import isEmpty from "lodash.isempty";
import { OpenDetailsPanelAction, OpenDetailsPanelPayload } from "../../../redux/details-panel/types";
import { FetchStoryItemAction, FetchStoryItemPayload } from "../../../redux/cache/types";
import backgroundImage from "../../../../static/planet.jpg";
import { PreviewKind } from "@radivision/graphql";
const VideoPanel = React.lazy(() => import("../../page-framework/video-panel"));

interface ComponentProps {
  item: any;
  list: any;
  previews: ItemPreviews;
  json: any;
}

interface MapStateProps {
  isPlayingFullScreen: boolean;
  data: any;
  isFetching: boolean;
}

interface MapDispatchProps {
  openDetailsPanel: (payload: OpenDetailsPanelPayload) => OpenDetailsPanelAction;
  fetchStoryItem: (payload: FetchStoryItemPayload) => FetchStoryItemAction;
}

type Props = ComponentProps & MapDispatchProps & MapStateProps;
const MiniHeroComponent = (props: Props) => {
  const {
    item: _item,
    list,
    previews,
    json,
    fetchStoryItem,
    data,
    isFetching,
    isPlayingFullScreen,
    openDetailsPanel,
  } = props;
  const [item, setItem] = React.useState(_item);
  const [retries, setRetries] = React.useState(0);
  const [playVideo, setPlayVideo] = React.useState(false);

  React.useEffect(() => {
    if (_item && !data && isEmpty(_item.peopleEntities) && retries < 3 && !isFetching) {
      setRetries(retries + 1);
      fetchStoryItem({ id: _item.id });
    }
  }, [_item, isFetching, fetchStoryItem, data, retries]);

  React.useEffect(() => {
    if (data && data.peopleEntities) {
      setItem(data);
    }
  }, [data]);

  const youtubeVideo = item?.externalId ? getVideoLinksFromExternalId(item.externalId) : null;
  const entity =
    (item?.peopleEntities && item?.peopleEntities[0] && item?.peopleEntities[0].entity) ||
    (item?.peopleEntities && item?.peopleEntities[1] && item?.peopleEntities[1].entity);
  const showTitle = json && json.title;

  const logo = previews?.LOGO;
  const arole = previews?.A_ROLE;
  const hasARole = list?.previews?.edges?.find((edge) => edge?.preview?.kind === PreviewKind.A_ROLE);
  const topLogo = hasARole ? arole : logo;
  const sideLogo = hasARole ? logo : null;
  return (
    <div className="roundedCorner">
      <div className="mini-hero-container" style={{ backgroundImage: `url(${backgroundImage})` }}>
        <div className="divShadow topShadow" />
        {item ? (
          <div className="miniContainer">
            {showTitle ? (
              <h1>{json.title}</h1>
            ) : (
              <ProgressiveImage
                src={`${topLogo?.requestedResolutionUrl || topLogo?.originalUrl}`}
                placeholder={topLogo?.placeHolderUrl}
              >
                {(src: any, LOADING: any, srcSetData: any) => (
                  <img
                    loading="lazy"
                    alt={`${item && item.title ? `${item.title}` : "epoxied-logo"}`}
                    src={src}
                    className={"title-logo"}
                  />
                )}
              </ProgressiveImage>
            )}

            <div className="hero-item">
              <div className="videoShadow">
                {previews?.HERO && (
                  <ProgressiveImage
                    src={`${
                      previews?.HERO?.requestedResolutionUrl
                        ? previews?.HERO?.requestedResolutionUrl
                        : previews?.HERO?.originalUrl
                    }`}
                    placeholder={previews?.HERO?.placeHolderUrl}
                  >
                    {(src: any, LOADING: any, srcSetData: any) => (
                      <img
                        loading="lazy"
                        alt={`${item && item.title ? `${item.title}` : "epoxied-logo"}`}
                        className="miniHero-logo hero"
                        src={src}
                      />
                    )}
                  </ProgressiveImage>
                )}
                <IsVisible onHide={() => setPlayVideo(false)} onShow={() => setPlayVideo(true)}>
                  <Suspense key="mini-hero-side-player" fallback={<div>Loading...</div>}>
                    {playVideo && !isPlayingFullScreen && (
                      <VideoPanel
                        json={{
                          youtube: youtubeVideo,
                        }}
                        disableAutoPlay={false}
                        mute={true}
                        loop={true}
                        videoPosition="FIT"
                      />
                    )}
                  </Suspense>
                </IsVisible>
              </div>
              <div className="info">
                {sideLogo ? (
                  <ProgressiveImage
                    src={`${sideLogo?.requestedResolutionUrl || sideLogo?.originalUrl}`}
                    placeholder={sideLogo?.placeHolderUrl}
                  >
                    {(src: any, LOADING: any, srcSetData: any) => (
                      <img
                        loading="lazy"
                        alt={`${item && item.title ? `${item.title}` : "epoxied-logo"}`}
                        style={{ width: "100%", maxWidth: "300px", height: "auto" }}
                        src={src}
                        className={"title-logo"}
                      />
                    )}
                  </ProgressiveImage>
                ) : null}
                <ProfilePictureName subject={entity} />
                <p style={{ cursor: "pointer" }}>
                  <Truncate lines={8} ellipsis={<span>...</span>}>
                    {item.description || item.title}
                  </Truncate>
                </p>
                <RdvButton
                  isOutline={true}
                  extraClassName="multiple"
                  onClick={() => {
                    openDetailsPanel({ item, list });

                    snowplowAnalytics()
                      .then((r) =>
                        r.trackPlayVideo({
                          category: item.id,
                          label: item.label,
                          property: item.externalId,
                        }),
                      )
                      .catch((r) => console.debug("SnowPlow not initialized."));
                  }}
                >
                  {SVG.play} Play
                </RdvButton>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const mapState = (state: AppState, props: ComponentProps) => ({
  isPlayingFullScreen: state.fullScreenVideo.isPlayingFullScreen,
  data: state.cache.store[props.item?.id],
  isFetching: state.cache.inProgress[props.item?.id],
  videoId: state.fullScreenVideo.videoId,
});
const mapDispatch = {
  fetchStoryItem: Actions.fetchStoryItem,
  openDetailsPanel: Actions.openDetailsPanel,
};
export const MiniHeroPanel = connect(mapState, mapDispatch)(MiniHeroComponent);
